import { useRef, useState, useMemo, useCallback, useEffect, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Badge,
  Text,
  useDisclosure,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
  SimpleGrid,
  Divider,
  Link,
  Spinner,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { CsatScoreDisplay } from '@/components/csat/CsatScoreDisplay'
import { apiClient } from '@/lib/api/client'

const basePath = '/ticket-management/all-tickets'

type TicketDetail = {
  id: number
  request_number: string
  user_id: number | null
  parent_ticket_id: number | null
  service_type_id: number | null
  description: string | null
  attachment_metadata: Array<{ name?: string; file_url?: string }> | null
  contact_number: string | null
  contact_name: string | null
  contact_email: string | null
  location?: string | null
  ticket_status_id: number
  slas_id: number
  for_approval: number
  assigned_to: number | null
  submitted_at: string | null
  resolved_at: string | null
  closed_at: string | null
  created_at: string | null
  created_at_human: string | null
  updated_at: string | null
  ticket_status_label?: string
  service_type_name?: string
  ticket_status?: { id: number; code: string; label: string }
  service_type?: { id: number; name: string; code: string }
  sla?: { id: number; severity: string; response_minutes: number; resolution_minutes: number }
  user?: { id: number; user_login: string; user_email: string }
  assigned_to_user?: { id: number; user_login: string; user_email: string }
  csat_score?: string | null
  csat_score_label?: string | null
  csat_submitted_at?: string | null
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return iso
  }
}

export default function TicketsForApprovalPage() {
  const navigate = useNavigate()
  const colors = useThemeColors()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess } = useToastNotification()
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure()

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null)
  const [detail, setDetail] = useState<TicketDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState<'approve' | 'reject' | null>(null)

  const tableParams = useMemo(() => ({ search: searchQuery, for_approval: 1 }), [searchQuery])

  const openDetail = useCallback((id: number) => {
    setSelectedTicketId(id)
    onDetailOpen()
  }, [onDetailOpen])

  useEffect(() => {
    if (!selectedTicketId || !isDetailOpen) return
    setDetailLoading(true)
    setDetail(null)
    apiClient
      .get(`${basePath}/${selectedTicketId}`)
      .then(({ data }) => setDetail(data.data ?? data))
      .catch((err) => showError(err as Error))
      .finally(() => setDetailLoading(false))
  }, [selectedTicketId, isDetailOpen, showError])

  const handleApprove = async () => {
    if (!selectedTicketId) return
    setActionLoading('approve')
    try {
      await apiClient.post(`${basePath}/approve/${selectedTicketId}`)
      showSuccess('Success', 'Ticket approved successfully')
      onDetailClose()
      setSelectedTicketId(null)
      setDetail(null)
      tableRef.current?.reload()
    } catch (err) {
      showError(err as Error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleReject = async () => {
    if (!selectedTicketId) return
    setActionLoading('reject')
    try {
      await apiClient.post(`${basePath}/reject/${selectedTicketId}`)
      showSuccess('Success', 'Ticket rejected')
      onDetailClose()
      setSelectedTicketId(null)
      setDetail(null)
      tableRef.current?.reload()
    } catch (err) {
      showError(err as Error)
    } finally {
      setActionLoading(null)
    }
  }

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: true,
      dataFields: {
        request_number: { name: 'Request #', withSort: true, maxWidth: '160px' },
        contact_name: { name: 'Contact', withSort: true, maxWidth: '140px' },
        contact_email: { name: 'Email', withSort: true, maxWidth: '180px' },
        location: { name: 'Location', withSort: false, maxWidth: '180px' },
        ticket_status_label: {
          name: 'Status',
          withSort: true,
          maxWidth: '120px',
          badge: {
            'New': 'blue',
            'Assigned': 'purple',
            'In Progress': 'teal',
            'Pending': 'yellow',
            'Cancelled': 'gray',
            'Resolved': 'green',
            'Closed': 'gray',
            'For Approval': 'orange',
            'Approved': 'green',
            'Rejected': 'red',
          },
        },
        service_type_name: { name: 'Service type', withSort: true, maxWidth: '140px' },
        category_name: { name: 'Category', withSort: false, maxWidth: '140px' },
        subcategory_name: { name: 'Sub-category', withSort: false, maxWidth: '140px' },
        item_name: { name: 'Item', withSort: false, maxWidth: '140px' },
        created_at_human: { name: 'Created', withSort: true, maxWidth: '160px' },
      },
      dataSource: basePath,
      primaryKey: 'id',
      softDelete: false,
      displayInModal: false,
      noRecordText: 'No tickets for approval',
      hideSelection: true,
      otherActions: [
        {
          name: 'View details',
          onClick: (row: { id: number }) => openDetail(row.id),
        },
      ],
    }),
    [openDetail]
  )

  const permissions = useMemo(
    () => ({
      can_edit: false,
      can_delete: false,
      can_view: true,
      can_create: false,
      can_export: false,
      can_import: false,
    }),
    []
  )

  const handleSearch = () => {
    tableRef.current?.applyFilter('search', searchQuery || '')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex={1} overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex={1} minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Tickets for Approval
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Ticket requests requiring approval (for_approval = Yes). View details and approve or reject.
              </Text>
            </CardHeader>

            <Box borderBottom="2px" borderColor={colors.border} mx={4} />
            <CardHeader py={2} pt={3} pb={2} flexShrink={0} px={5}>
              <HStack spacing={4}>
                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by request #, contact, email..."
                      value={searchQuery}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                      onKeyDown={handleKeyDown}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody p={0} pt={1} pb={4} mx={5} flex={1} overflow="hidden" display="flex" flexDirection="column" minH={0}>
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  defaultSort={{ order: 'created_at', sort: 'desc' }}
                  params={tableParams}
                  access={permissions}
                  onRowClick={(row: { id: number }) =>
                    navigate(`/ticket-management/all-tickets/${row.id}/update-status`)
                  }
                />
              </Box>
            </CardBody>
          </Card>

          <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="2xl" scrollBehavior="inside" isCentered>
            <ModalOverlay />
            <ModalContent maxH="90vh">
              <ModalHeader>
                Ticket details {detail?.request_number && `— ${detail.request_number}`}
              </ModalHeader>
              <ModalBody pb={4}>
                {detailLoading && (
                  <VStack py={8}>
                    <Spinner size="lg" color="blue.500" />
                    <Text>Loading...</Text>
                  </VStack>
                )}
                {!detailLoading && detail && (
                  <VStack align="stretch" spacing={4}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted} mb={1}>Request #</Text>
                        <Text fontWeight="medium">{detail.request_number}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted} mb={1}>Status</Text>
                        <Badge colorScheme="orange">{detail.ticket_status?.label ?? detail.ticket_status_label ?? '—'}</Badge>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted} mb={1}>Service type</Text>
                        <Text>{detail.service_type?.name ?? detail.service_type_name ?? '—'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted} mb={1}>Submitted at</Text>
                        <Text>{formatDate(detail.submitted_at)}</Text>
                      </Box>
                      <CsatScoreDisplay
                        score={detail.csat_score}
                        scoreLabel={detail.csat_score_label}
                        submittedAt={detail.csat_submitted_at}
                        compact
                      />
                    </SimpleGrid>

                    <Box>
                      <Text fontSize="xs" color={colors.textMuted} mb={1}>Description</Text>
                      <Text whiteSpace="pre-wrap" bg={colors.sectionBg} p={3} borderRadius="md">
                        {detail.description || '—'}
                      </Text>
                    </Box>

                    <Divider />
                    <Text fontWeight="semibold" fontSize="sm">Contact</Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted}>Name</Text>
                        <Text>{detail.contact_name || '—'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted}>Email</Text>
                        <Text>{detail.contact_email || '—'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted}>Phone</Text>
                        <Text>{detail.contact_number || '—'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted}>Location</Text>
                        <Text>{detail.location?.trim() ? detail.location : '—'}</Text>
                      </Box>
                    </SimpleGrid>

                    <Divider />
                    <Text fontWeight="semibold" fontSize="sm">Assignment & SLA</Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted}>Submitted by</Text>
                        <Text>{detail.user ? `${detail.user.user_login} (${detail.user.user_email})` : '—'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textMuted}>Assigned to</Text>
                        <Text>
                          {detail.assigned_to_user
                            ? `${detail.assigned_to_user.user_login} (${detail.assigned_to_user.user_email})`
                            : '—'}
                        </Text>
                      </Box>
                      {detail.sla && (
                        <Box>
                          <Text fontSize="xs" color={colors.textMuted}>SLA (severity / response / resolution)</Text>
                          <Text>{detail.sla.severity} — {detail.sla.response_minutes} min / {detail.sla.resolution_minutes} min</Text>
                        </Box>
                      )}
                    </SimpleGrid>

                    {detail.attachment_metadata && detail.attachment_metadata.length > 0 && (
                      <>
                        <Divider />
                        <Text fontWeight="semibold" fontSize="sm">Attachments</Text>
                        <VStack align="stretch" spacing={1}>
                          {detail.attachment_metadata.map((file: { name?: string; file_url?: string }, i: number) => (
                            <Box key={i}>
                              {file.file_url ? (
                                <Link href={file.file_url} isExternal color="blue.500">
                                  {file.name || 'File'}
                                </Link>
                              ) : (
                                <Text>{file.name || 'File'}</Text>
                              )}
                            </Box>
                          ))}
                        </VStack>
                      </>
                    )}

                    <Box>
                      <Text fontSize="xs" color={colors.textMuted}>Created</Text>
                      <Text>{formatDate(detail.created_at)}</Text>
                    </Box>
                  </VStack>
                )}
                {!detailLoading && !detail && selectedTicketId && (
                  <Text color={colors.textSecondary}>Could not load ticket details.</Text>
                )}
              </ModalBody>
              {detail && !detailLoading && (
                <ModalFooter borderTopWidth="1px" borderColor={colors.border}>
                  <Button variant="ghost" mr={3} onClick={onDetailClose}>
                    Close
                  </Button>
                  <Button
                    colorScheme="red"
                    variant="outline"
                    onClick={handleReject}
                    isLoading={actionLoading === 'reject'}
                    mr={2}
                  >
                    Reject
                  </Button>
                  <Button
                    colorScheme="green"
                    onClick={handleApprove}
                    isLoading={actionLoading === 'approve'}
                  >
                    Approve
                  </Button>
                </ModalFooter>
              )}
            </ModalContent>
          </Modal>
        </VStack>
      </Box>
    </AdminLayout>
  )
}
